import QRCode from 'qrcode';

const APP_NAME = 'Saral Asset Manager';
const TIMEZONE = 'Asia/Kolkata';
const LOCALE = 'en-IN';

const escapeHtml = (value) => String(value ?? '')
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;')
  .replace(/'/g, '&#39;');

const normalizeText = (value) => String(value ?? '')
  .replace(/\s+/g, ' ')
  .trim();

const dedupeFields = (rows = [], blockedValues = new Set()) => {
  const seen = new Set();

  return rows.filter((row) => {
    const label = normalizeText(row?.label);
    const value = normalizeText(row?.value);

    if (!label || !value) return false;

    const valueKey = value.toLowerCase();
    const pairKey = `${label.toLowerCase()}::${valueKey}`;
    if (seen.has(pairKey) || blockedValues.has(valueKey)) {
      return false;
    }

    seen.add(pairKey);
    return true;
  });
};

const dedupeParties = (parties = []) => {
  const seen = new Set();

  return parties.filter((party) => {
    const title = normalizeText(party?.title);
    const lines = (party?.lines || []).map(normalizeText).filter(Boolean);
    if (!title || !lines.length) return false;

    const key = `${title.toLowerCase()}::${lines.join('|').toLowerCase()}`;
    if (seen.has(key)) {
      return false;
    }

    seen.add(key);
    return true;
  }).map((party) => ({
    ...party,
    lines: (party.lines || []).map(normalizeText).filter(Boolean)
  }));
};

export const formatDateTime = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);

  return new Intl.DateTimeFormat(LOCALE, {
    timeZone: TIMEZONE,
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  }).format(date);
};

export const formatCurrency = (value) => {
  const amount = Number(value ?? 0);
  return new Intl.NumberFormat(LOCALE, {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 2
  }).format(Number.isFinite(amount) ? amount : 0);
};

const toDisplayRows = (rows = []) => rows
  .filter((row) => row && row.label && row.value != null && row.value !== '')
  .map((row) => `
    <tr>
      <td>${escapeHtml(row.label)}</td>
      <td>${escapeHtml(row.value)}</td>
    </tr>
  `)
  .join('');

const toPartyCards = (parties = []) => parties
  .filter((party) => party && party.title)
  .map((party) => `
    <section class="party-card">
      <div class="party-title">${escapeHtml(party.title)}</div>
      ${(party.lines || []).filter(Boolean).map((line) => `<div class="party-line">${escapeHtml(line)}</div>`).join('')}
    </section>
  `)
  .join('');

const toItemsTable = (items = []) => {
  if (!items.length) return '';
  const showReturnedStatus = items.some((item) => item?.returnedStatus);

  return `
    <section class="block items-block">
      <div class="section-title">Asset Details</div>
      <table class="items-table">
        <thead>
          <tr>
            <th>#</th>
            <th>Asset</th>
            <th>Type</th>
            <th>Saral Asset ID</th>
            <th>Serial Number</th>
            ${showReturnedStatus ? '<th>Returned Status</th>' : ''}
          </tr>
        </thead>
        <tbody>
          ${items.map((item, index) => `
            <tr>
              <td>${index + 1}</td>
              <td>${escapeHtml(item.assetName || '-')}</td>
              <td>${escapeHtml(item.assetType || '-')}</td>
              <td>${escapeHtml(item.saralAssetId || '-')}</td>
              <td>${escapeHtml(item.serialNumber || '-')}</td>
              ${showReturnedStatus ? `<td>${escapeHtml(item.returnedStatus || '-')}</td>` : ''}
            </tr>
          `).join('')}
        </tbody>
      </table>
    </section>
  `;
};

const getReceiverName = (parties = []) => {
  const preferredTitles = ['assigned to', 'transferred to', 'submitted by', 'inventory holder'];

  for (const title of preferredTitles) {
    const match = parties.find((party) => normalizeText(party?.title).toLowerCase() === title);
    const name = normalizeText(match?.lines?.[0]);
    if (name) return name;
  }

  for (const party of parties) {
    const name = normalizeText(party?.lines?.[0]);
    if (name) return name;
  }

  return '';
};

const buildHtml = ({
  accentColor = '#14532d',
  category = 'Electronic Receipt',
  title,
  subtitle,
  receiptNumber,
  issuedAt,
  status,
  summaryRows = [],
  metadataRows = [],
  parties = [],
  items = [],
  footerNote,
  verificationUrl,
  signaturePreview,
  generatedBy,
  qrCodeDataUrl
}) => {
  const issuedAtText = formatDateTime(issuedAt);
  const statusText = normalizeText(status || 'Recorded');
  const generatedByText = normalizeText(generatedBy || APP_NAME);
  const blockedValues = new Set([
    normalizeText(receiptNumber).toLowerCase(),
    issuedAtText.toLowerCase(),
    statusText.toLowerCase(),
    generatedByText.toLowerCase(),
    APP_NAME.toLowerCase(),
    normalizeText(signaturePreview).toLowerCase(),
    normalizeText(verificationUrl).toLowerCase()
  ].filter(Boolean));

  const cleanSummaryRows = dedupeFields(summaryRows, blockedValues);
  const cleanMetadataRows = dedupeFields(metadataRows, new Set([
    ...blockedValues,
    ...cleanSummaryRows.map((row) => normalizeText(row.value).toLowerCase())
  ]));
  const cleanParties = dedupeParties(parties);
  const hasSummaryRows = cleanSummaryRows.length > 0;
  const hasMetadataRows = cleanMetadataRows.length > 0;
  const receiverName = getReceiverName(cleanParties) || '________________________';

  return `
  <!doctype html>
  <html>
    <head>
      <meta charset="utf-8" />
      <title>${escapeHtml(title)} - ${escapeHtml(receiptNumber)}</title>
      <style>
        :root {
          --accent: ${accentColor};
          --accent-soft: #e9f5ee;
          --ink: #172033;
          --muted: #5a6478;
          --border: #d7dce5;
          --surface: #ffffff;
          --surface-alt: #f5f7fb;
          --surface-soft: #f8fafc;
        }
        * { box-sizing: border-box; }
        body { margin: 0; background: linear-gradient(180deg, #eef3f8 0%, #e6edf5 100%); color: var(--ink); font-family: "Segoe UI", Calibri, Arial, sans-serif; padding: 24px; }
        .sheet { max-width: 980px; margin: 0 auto; background: var(--surface); border: 1px solid rgba(20, 32, 51, 0.08); box-shadow: 0 18px 48px rgba(15, 23, 42, 0.12); border-radius: 24px; overflow: hidden; }
        .toolbar { display: flex; justify-content: flex-end; gap: 12px; padding: 18px 24px 0; }
        .toolbar button { border: 0; border-radius: 999px; padding: 10px 18px; font-size: 14px; cursor: pointer; }
        .toolbar .primary { background: var(--accent); color: white; }
        .toolbar .secondary { background: var(--surface-alt); color: var(--ink); border: 1px solid var(--border); }
        .content { padding: 0 32px 28px; }
        .hero { display: grid; grid-template-columns: 1.4fr 1fr; gap: 24px; padding: 28px 0 24px; border-bottom: 2px solid var(--border); align-items: start; }
        .brand { display: flex; flex-direction: column; gap: 10px; }
        .title { margin: 0; font-size: 30px; line-height: 1.15; }
        .subtitle { margin: 0; color: var(--muted); font-size: 14px; line-height: 1.5; }
        .meta-panel { display: grid; grid-template-columns: 1fr 104px; gap: 16px; align-items: start; background: linear-gradient(180deg, var(--surface-soft), white); border: 1px solid var(--border); border-radius: 18px; padding: 18px 20px; }
        .meta-panel .meta-row { display: flex; justify-content: space-between; gap: 16px; padding: 7px 0; border-bottom: 1px dashed rgba(90, 100, 120, 0.18); }
        .meta-panel .meta-row:last-child { border-bottom: 0; padding-bottom: 0; }
        .meta-label { color: var(--muted); font-size: 13px; }
        .meta-value { font-weight: 600; font-size: 13px; text-align: right; word-break: break-word; }
        .status-pill { display: inline-flex; align-items: center; justify-content: center; width: fit-content; margin-top: 12px; padding: 7px 12px; border-radius: 999px; background: var(--accent); color: white; font-size: 12px; font-weight: 700; letter-spacing: 0.04em; text-transform: uppercase; }
        .receipt-qr img { width: 96px; height: 96px; border: 1px solid var(--border); padding: 5px; border-radius: 10px; background: white; }
        .grid { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; margin-top: 22px; }
        .block { margin-top: 22px; }
        .section-title { margin-bottom: 12px; color: var(--accent); font-size: 13px; font-weight: 800; letter-spacing: 0.08em; text-transform: uppercase; }
        .detail-table, .items-table { width: 100%; border-collapse: collapse; border: 1px solid var(--border); border-radius: 16px; overflow: hidden; }
        .detail-table td, .items-table th, .items-table td { border-bottom: 1px solid var(--border); padding: 11px 14px; font-size: 14px; vertical-align: top; }
        .detail-table tr:last-child td, .items-table tr:last-child td { border-bottom: 0; }
        .detail-table td:first-child { width: 42%; background: var(--surface-alt); color: var(--muted); font-weight: 600; }
        .items-table th { background: var(--surface-alt); text-align: left; font-size: 13px; }
        .party-stack { display: grid; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); gap: 16px; margin-top: 22px; }
        .party-card { border: 1px solid var(--border); border-radius: 16px; padding: 16px; background: linear-gradient(180deg, white, var(--surface-alt)); }
        .party-title { color: var(--accent); font-size: 13px; font-weight: 800; letter-spacing: 0.08em; text-transform: uppercase; margin-bottom: 10px; }
        .party-line { font-size: 14px; line-height: 1.5; }
        .signature-block { margin-top: 28px; padding-top: 22px; border-top: 1px solid var(--border); }
        .signature-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 36px; }
        .signature-card { min-height: 112px; display: flex; flex-direction: column; justify-content: flex-end; padding: 18px 20px 14px; border: 1px solid var(--border); border-radius: 18px; background: linear-gradient(180deg, white, var(--surface-soft)); }
        .signature-line { border-top: 1px solid #7b8798; padding-top: 10px; }
        .signature-role { color: var(--accent); font-size: 12px; font-weight: 800; letter-spacing: 0.08em; text-transform: uppercase; }
        .signature-name { margin-top: 8px; font-size: 14px; font-weight: 600; }
        .signature-hint { margin-top: 4px; color: var(--muted); font-size: 12px; }
        .footer { margin-top: 28px; padding-top: 18px; border-top: 1px solid var(--border); color: var(--muted); font-size: 12px; line-height: 1.6; }
        a { color: var(--accent); text-decoration: none; }
        @page { size: A4; margin: 5mm; }
        @media (max-width: 860px) {
          body { padding: 12px; }
          .content { padding: 0 18px 20px; }
          .hero, .grid { grid-template-columns: 1fr; }
          .meta-panel { grid-template-columns: 1fr; }
          .receipt-qr { order: -1; }
          .sheet { border-radius: 16px; }
          .toolbar { padding: 14px 18px 0; }
        }
        @media print {
          body { background: white; padding: 0; font-size: 11px; }
          .sheet { max-width: none; box-shadow: none; border: 0; border-radius: 0; }
          .toolbar { display: none; }
          .content { min-height: calc(297mm - 10mm); padding: 0; display: flex; flex-direction: column; }
          .hero { gap: 14px; padding: 0 0 12px; border-bottom-width: 1px; }
          .brand { gap: 6px; }
          .title { font-size: 22px; line-height: 1.1; }
          .subtitle, .parties-block, .footer { display: none; }
          .meta-panel { grid-template-columns: 82px 1fr; gap: 12px; padding: 10px 12px; border-radius: 10px; }
          .receipt-qr { order: -1; }
          .meta-panel .meta-row { gap: 10px; padding: 4px 0; }
          .meta-label, .meta-value { font-size: 10px; }
          .status-pill { margin-top: 7px; padding: 4px 8px; font-size: 9px; }
          .receipt-qr img { width: 76px; height: 76px; padding: 3px; border-radius: 6px; }
          .grid { gap: 12px; margin-top: 12px; }
          .block { margin-top: 12px; break-inside: avoid; page-break-inside: avoid; }
          .section-title { margin-bottom: 6px; font-size: 10px; }
          .detail-table, .items-table { border-radius: 8px; }
          .detail-table td, .items-table th, .items-table td { padding: 6px 8px; font-size: 10.5px; }
          .items-table th { font-size: 10px; }
          .party-stack { gap: 10px; margin-top: 12px; }
          .party-card { padding: 9px 10px; border-radius: 8px; }
          .party-title { margin-bottom: 5px; font-size: 10px; }
          .party-line { font-size: 10.5px; line-height: 1.35; }
          .signature-block { margin-top: auto; padding-top: 10px; break-inside: avoid; page-break-inside: avoid; }
          .signature-grid { gap: 18px; }
          .signature-card { min-height: 70px; padding: 10px 12px 8px; border-radius: 8px; }
          .signature-line { padding-top: 7px; }
          .signature-role { font-size: 9px; }
          .signature-name { margin-top: 5px; font-size: 10.5px; }
          .signature-hint { margin-top: 2px; font-size: 9px; }
        }
      </style>
    </head>
    <body>
      <div class="sheet">
        <div class="toolbar">
          <button class="secondary" onclick="window.close()">Close</button>
          <button class="primary" onclick="window.print()">Print Receipt</button>
        </div>
        <div class="content">
          <section class="hero">
            <div class="brand">
              <h1 class="title">${escapeHtml(title)}</h1>
              <p class="subtitle">${escapeHtml(subtitle || `${APP_NAME} issued this electronic receipt`)}</p>
            </div>
            <div class="meta-panel">
              <div>
                <div class="meta-row"><div class="meta-label">Receipt No.</div><div class="meta-value">${escapeHtml(receiptNumber)}</div></div>
                <div class="meta-row"><div class="meta-label">Issued At</div><div class="meta-value">${escapeHtml(issuedAtText)}</div></div>
                <div class="status-pill">${escapeHtml(statusText)}</div>
              </div>
              <div class="receipt-qr">${qrCodeDataUrl ? `<img src="${qrCodeDataUrl}" alt="Receipt verification QR code" />` : ''}</div>
            </div>
          </section>

          ${(hasSummaryRows || hasMetadataRows) ? `
            <div class="grid">
            ${hasSummaryRows ? `
            <section class="block summary-block">
              <div class="section-title">Transaction Summary</div>
              <table class="detail-table"><tbody>${toDisplayRows(cleanSummaryRows)}</tbody></table>
            </section>
            ` : ''}
            ${hasMetadataRows ? `
            <section class="block metadata-block">
              <div class="section-title">Reference Details</div>
              <table class="detail-table"><tbody>${toDisplayRows(cleanMetadataRows)}</tbody></table>
            </section>
            ` : ''}
            </div>
          ` : ''}

          ${cleanParties.length ? `
            <section class="block parties-block">
              <div class="section-title">Parties</div>
              <div class="party-stack">${toPartyCards(cleanParties)}</div>
            </section>
          ` : ''}

          ${toItemsTable(items)}

          <section class="signature-block">
            <div class="section-title">Signatures</div>
            <div class="signature-grid">
              <div class="signature-card">
                <div class="signature-line">
                  <div class="signature-role">Asset Manager Signature</div>
                  <div class="signature-name">Name:</div>
                </div>
              </div>
              <div class="signature-card">
                <div class="signature-line">
                  <div class="signature-role">Receiver Signature</div>
                  <div class="signature-name">Name: ${escapeHtml(receiverName)}</div>
                  <div class="signature-hint">Sign after confirming asset details.</div>
                </div>
              </div>
            </div>
          </section>

          <div class="footer">${escapeHtml(footerNote || 'Verify this receipt using the QR code or receipt number before treating it as authentic.')}</div>
        </div>
      </div>
      <script>
        window.addEventListener('load', function () {
          setTimeout(function () {
            if (window.__AUTO_PRINT__ !== false) {
              window.print();
            }
          }, 250);
        });
      </script>
    </body>
  </html>
`;
};

export const openReceiptWindow = async (receipt) => {
  try {
    if (typeof navigator !== 'undefined' && /jsdom/i.test(navigator.userAgent || '')) {
      return false;
    }

    const receiptWindow = window.open('about:blank', '_blank', 'width=1100,height=900');
    if (!receiptWindow) return false;

    const qrCodeDataUrl = receipt?.verificationUrl
      ? await QRCode.toDataURL(receipt.verificationUrl, { width: 160, margin: 1 })
      : '';

    const html = buildHtml({ ...receipt, qrCodeDataUrl });
    receiptWindow.document.open('text/html', 'replace');
    receiptWindow.document.write(html);
    receiptWindow.document.close();
    receiptWindow.__AUTO_PRINT__ = receipt.autoPrint !== false;
    receiptWindow.document.title = `${receipt?.title || 'Receipt'} - ${receipt?.receiptNumber || APP_NAME}`;
    if (receiptWindow.history?.replaceState) {
      const printablePath = `/receipt-print/${encodeURIComponent(receipt?.receiptNumber || 'preview')}`;
      receiptWindow.history.replaceState({ receiptNumber: receipt?.receiptNumber || null }, '', printablePath);
    }
    receiptWindow.focus?.();
    return true;
  } catch (error) {
    console.error('Failed to open receipt window:', error);
    return false;
  }
};
